/**
 * Utility for handling paste operations in contentEditable elements
 * Handles large HTML payloads from applications like Google Docs, Figma, etc.
 */

export type PasteMode = "plain" | "basic-html";

/**
 * Handles paste events for contentEditable elements
 * @param e - The clipboard event
 * @param pasteMode - How to handle the pasted content
 * @param onContentChange - Optional callback when content changes
 * @param autoGrow - Optional callback to adjust element height
 */
export const handlePaste = (
  e: React.ClipboardEvent<HTMLDivElement>,
  pasteMode: PasteMode = "plain",
  onContentChange?: (content: string) => void,
  autoGrow?: () => void
) => {
  e.preventDefault();
  
  if (pasteMode === "plain") {
    const text = e.clipboardData.getData("text/plain");
    const safe = escapeHtml(text).replace(/\n/g, "<br>");
    document.execCommand("insertHTML", false, safe);
  } else {
    const html = e.clipboardData.getData("text/html") || e.clipboardData.getData("text/plain");
    const sanitized = sanitizeBasicHtml(html);
    document.execCommand("insertHTML", false, sanitized);
  }
  
  // Call callbacks if provided
  onContentChange?.(document.execCommand("insertHTML", false, "") ? "" : "");
  autoGrow?.();
};

/**
 * Escape an HTML string so that tags are shown literally
 */
export const escapeHtml = (html: string): string =>
  html
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/**
 * Sanitize HTML to allow only basic formatting tags
 * Very small allowlist: <b><strong><i><em><u><br><ul><ol><li><a href>
 */
export const sanitizeBasicHtml = (input: string): string => {
  const div = document.createElement("div");
  div.innerHTML = input;
  
  const ALLOWED = new Set(["B", "STRONG", "I", "EM", "U", "BR", "UL", "OL", "LI", "A"]);
  
  const walk = (node: Node) => {
    const parent = node.parentNode;
    if (!parent) return;
    
    if (node.nodeType === 1) {
      const el = node as HTMLElement;
      if (!ALLOWED.has(el.tagName)) {
        // Replace non-allowed element with its text content
        parent.replaceChild(document.createTextNode(el.textContent || ""), el);
        return;
      }
      
      // Clean attributes - only keep href for links
      for (const attr of Array.from(el.attributes)) {
        if (!(el.tagName === "A" && attr.name.toLowerCase() === "href")) {
          el.removeAttribute(attr.name);
        }
      }
    }
    
    for (const child of Array.from(node.childNodes)) {
      walk(child);
    }
  };
  
  for (const child of Array.from(div.childNodes)) {
    walk(child);
  }
  
  return div.innerHTML;
};

